import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import ToggleSwitch from '../../components/ui/ToggleSwitch';
import TrainingHostLobbyContent from '../../components/training/TrainingHostLobbyContent';
import {
  teacherTrainingHostPath,
  teacherTrainingProgramEditPath,
  teacherTrainingProgramPath,
  teacherTrainingProgramRegisterPath,
} from '../../config/contentRoutes';
import {
  formatTrainingHostError,
  getTrainingHostByOwnerUid,
  isValidTrainingHostSlug,
  normalizeTrainingHostSlug,
  subscribeTrainingProgramsByHost,
  upsertTrainingHost,
} from '../../utils/trainingHost';
import { importMaruLegacyTrainingPrograms, syncLobbyAutoLegacyPrograms } from '../../utils/importLegacyTrainingPrograms';
import {
  FEATURE_TRAINING_INTERNAL_LINKS,
  FEATURE_TRAINING_LEGACY_AUTO_SYNC,
} from '../../config/featureFlags';
import type { TrainingHost, TrainingProgram, TrainingProgramStatus } from '../../types/trainingHost';

const TrainingLobbyPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [host, setHost] = useState<TrainingHost | null>(null);
  const [programs, setPrograms] = useState<TrainingProgram[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPublishSettings, setShowPublishSettings] = useState(false);

  const [displayName, setDisplayName] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [bio, setBio] = useState('');
  const [publicSlug, setPublicSlug] = useState('');
  const [isPublic, setIsPublic] = useState(true);

  const [newTitle, setNewTitle] = useState('');
  const [newStatus, setNewStatus] = useState<TrainingProgramStatus>('upcoming');
  const [creatingProgram, setCreatingProgram] = useState(false);

  useEffect(() => {
    if (!currentUser?.uid) return;

    let unsub: (() => void) | undefined;

    const load = async () => {
      try {
        const hostData = await getTrainingHostByOwnerUid(currentUser.uid);
        if (hostData) {
          setHost(hostData);
          setPublicSlug(hostData.publicSlug);
          setDisplayName(hostData.displayName);
          setSchoolName(hostData.schoolName ?? '');
          setBio(hostData.bio ?? '');
          setIsPublic(hostData.isPublic);

          unsub = subscribeTrainingProgramsByHost(
            hostData.id,
            { includePrivate: true },
            setPrograms,
            (err) => {
              console.error(err);
              toast.error(formatTrainingHostError(err));
            }
          );

          if (FEATURE_TRAINING_LEGACY_AUTO_SYNC) {
            importMaruLegacyTrainingPrograms(currentUser.uid, hostData.id)
              .then((result) => {
                if (result.added > 0) {
                  toast.info(`새 연수 ${result.added}건이 목록에 추가되었습니다.`);
                }
              })
              .catch((err) => {
                console.warn('레거시 연수 동기화 실패:', err);
              });

            syncLobbyAutoLegacyPrograms(currentUser.uid, hostData.id)
              .then((result) => {
                if (result.added > 0) {
                  toast.success(`연수 ${result.added}개가 로비에 추가되었습니다.`);
                }
              })
              .catch((err) => console.error(err));
          }
        } else {
          setDisplayName(currentUser.name || '');
          setShowPublishSettings(true);
        }
      } catch (err) {
        console.error(err);
        toast.error(formatTrainingHostError(err));
      } finally {
        setLoading(false);
      }
    };

    load();
    return () => unsub?.();
  }, [currentUser?.uid, currentUser?.name]);

  const normalizedSlug = normalizeTrainingHostSlug(publicSlug);
  const slugPreview = normalizedSlug || 'my-slug';
  const publicPath = `/teacher-training/h/${slugPreview}`;

  const handleProfileChange = (field: 'displayName' | 'schoolName' | 'bio' | 'publicSlug', value: string) => {
    if (field === 'displayName') setDisplayName(value);
    else if (field === 'schoolName') setSchoolName(value);
    else if (field === 'bio') setBio(value);
    else if (field === 'publicSlug') setPublicSlug(value);
  };

  const ensureHostSaved = async (): Promise<TrainingHost | null> => {
    if (!currentUser?.uid) return null;
    if (!displayName.trim()) {
      toast.error('이름을 입력해 주세요.');
      return null;
    }
    if (!isValidTrainingHostSlug(normalizedSlug)) {
      toast.error('공개 주소 형식을 확인해 주세요. (2~40자, 영문·숫자·한글·하이픈)');
      setShowPublishSettings(true);
      return null;
    }

    const saved = await upsertTrainingHost(currentUser.uid, {
      publicSlug: normalizedSlug,
      displayName: displayName.trim(),
      schoolName: schoolName.trim() || undefined,
      bio: bio.trim() || undefined,
      isPublic,
    });
    setHost(saved);

    if (FEATURE_TRAINING_LEGACY_AUTO_SYNC) {
      void importMaruLegacyTrainingPrograms(currentUser.uid, saved.id).catch((err) => {
        console.warn('레거시 연수 동기화 실패:', err);
      });
    }

    return saved;
  };

  const handleSave = async () => {
    if (!currentUser?.uid) return;
    setSaving(true);
    try {
      await ensureHostSaved();
      toast.success('로비가 저장되었습니다. 방문자에게 이렇게 보입니다.');
    } catch (err) {
      console.error(err);
      toast.error(formatTrainingHostError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleGoRegister = async (mode: 'editor' | 'link') => {
    if (mode === 'link' && !FEATURE_TRAINING_INTERNAL_LINKS) {
      toast.info('내부 페이지 연결은 사용하지 않습니다. 웹 에디터로 연수를 추가해 주세요.');
      return;
    }
    if (!newTitle.trim()) {
      toast.error('연수 제목을 입력해 주세요.');
      return;
    }

    setCreatingProgram(true);
    try {
      const savedHost = host ?? (await ensureHostSaved());
      if (!savedHost) return;

      navigate(teacherTrainingProgramRegisterPath(mode), {
        state: { title: newTitle.trim(), status: newStatus },
      });
      setNewTitle('');
    } catch (err) {
      console.error(err);
      toast.error(formatTrainingHostError(err));
    } finally {
      setCreatingProgram(false);
    }
  };

  const openProgram = (program: TrainingProgram) => {
    if (!isValidTrainingHostSlug(normalizedSlug)) return;
    if ((program.contentType === 'editor' || program.contentHtml) && program.programSlug) {
      navigate(teacherTrainingProgramPath(normalizedSlug, program.programSlug));
      return;
    }
    if (!program.path) return;
    if (program.linkType === 'external') {
      window.open(program.path, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(program.path);
  };

  if (loading) {
    return (
      <div
        style={{
          width: '100%',
          padding: '32px 24px 48px',
          boxSizing: 'border-box',
          fontFamily: 'var(--font-family-primary)',
        }}
      >
        <p style={{ color: 'var(--Color-text-normal)' }}>불러오는 중…</p>
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        minHeight: '100%',
        boxSizing: 'border-box',
        fontFamily: 'var(--font-family-primary)',
        backgroundColor: 'var(--Color-background-main)',
      }}
    >
      <div
        style={{
          width: '100%',
          padding: '32px 24px 48px',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <Card
          padding="0"
          borderRadius="16px"
          background="var(--Color-background-normal)"
          width="100%"
          style={{ border: '1px solid var(--Color-line-normal)', overflow: 'hidden' }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) auto',
              gap: '16px',
              alignItems: 'center',
              width: '100%',
              boxSizing: 'border-box',
              padding: '16px 24px',
              borderBottom: '1px solid var(--Color-line-normal)',
              background: 'var(--Color-background-normal)',
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--Color-fill-brand-color)', marginBottom: '4px' }}>
                방문자에게 보이는 화면
              </div>
              <p style={{ margin: 0, fontSize: '14px', color: 'var(--Color-text-normal)', lineHeight: 1.5 }}>
                아래 내용이 그대로 공개됩니다. 텍스트와 연수 목록을 직접 수정하세요.
              </p>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', justifyContent: 'flex-end' }}>
              <Button variant="outline" size="medium" onClick={() => setShowPublishSettings((v) => !v)}>
                {showPublishSettings ? '공개 설정 닫기' : '공개 설정'}
              </Button>
              {isValidTrainingHostSlug(normalizedSlug) && (
                <Button variant="outline" size="medium" onClick={() => navigate(teacherTrainingHostPath(normalizedSlug))}>
                  방문자 화면 열기
                </Button>
              )}
              <Button variant="primary" size="medium" onClick={handleSave} disabled={saving}>
                {saving ? '저장 중…' : '저장'}
              </Button>
            </div>
          </div>

          {showPublishSettings && (
            <div
              style={{
                padding: '16px 24px',
                borderBottom: '1px solid var(--Color-line-normal)',
                background: 'var(--Color-background-main)',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--Color-text-strong)' }}>공개 주소·노출</div>
                <input
                  value={publicSlug}
                  onChange={(e) => setPublicSlug(e.target.value)}
                  placeholder="공개 주소 (예: han-jiho)"
                  style={{
                    width: '100%',
                    maxWidth: '400px',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid var(--Color-line-normal)',
                    fontFamily: 'var(--font-family-primary)',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                  }}
                />
                <p style={{ margin: 0, fontSize: '13px', color: 'var(--Color-text-normal)' }}>{publicPath}</p>
                <ToggleSwitch label="허브 목록에 공개" checked={isPublic} onChange={setIsPublic} />
              </div>
            </div>
          )}

          <div style={{ padding: '24px', boxSizing: 'border-box', width: '100%' }}>
            <TrainingHostLobbyContent
              mode="edit"
              photoURL={host?.photoURL}
              profile={{ displayName, schoolName, bio, publicSlug }}
              programs={programs}
              showPrivatePrograms
              publicPath={publicPath}
              onProfileChange={handleProfileChange}
              onOpenProgram={openProgram}
              onEditProgram={(p) => navigate(teacherTrainingProgramEditPath(p.id))}
              onPreviewProgram={(p) => {
                if (p.programSlug && isValidTrainingHostSlug(normalizedSlug)) {
                  navigate(teacherTrainingProgramPath(normalizedSlug, p.programSlug));
                }
              }}
              addProgramSlot={
                <Card
                  padding="16px 20px"
                  borderRadius="12px"
                  background="var(--Color-background-main)"
                  width="100%"
                  style={{ border: '2px dashed var(--Color-line-normal)', boxSizing: 'border-box' }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--Color-text-strong)' }}>연수 추가</div>
                    <p style={{ margin: 0, fontSize: '13px', color: 'var(--Color-text-normal)', lineHeight: 1.5 }}>
                      웹 에디터로 연수 페이지를 작성해 로비에 공개합니다.
                    </p>
                    <input
                      value={newTitle}
                      onChange={(e) => setNewTitle(e.target.value)}
                      placeholder="새 연수 제목"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        border: '1px solid var(--Color-line-normal)',
                        fontFamily: 'var(--font-family-primary)',
                        fontSize: '15px',
                        boxSizing: 'border-box',
                      }}
                    />
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                      <span style={{ fontSize: '14px', color: 'var(--Color-text-normal)' }}>상태</span>
                      {(['upcoming', 'active', 'completed'] as TrainingProgramStatus[]).map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => setNewStatus(s)}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '8px',
                            border:
                              newStatus === s ? '2px solid var(--Color-fill-brand-color)' : '1px solid var(--Color-line-normal)',
                            background: newStatus === s ? 'var(--Color-fill-hover)' : 'transparent',
                            cursor: 'pointer',
                            fontSize: '13px',
                          }}
                        >
                          {s === 'upcoming' ? '예정' : s === 'active' ? '진행 중' : '완료'}
                        </button>
                      ))}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      <Button
                        variant="primary"
                        size="medium"
                        onClick={() => handleGoRegister('editor')}
                        disabled={creatingProgram}
                      >
                        {creatingProgram ? '만드는 중…' : '에디터로 연수 추가'}
                      </Button>
                      {FEATURE_TRAINING_INTERNAL_LINKS && (
                        <Button
                          variant="outline"
                          size="medium"
                          onClick={() => handleGoRegister('link')}
                          disabled={creatingProgram}
                        >
                          링크 등록
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              }
            />

            {programs.length === 0 && (
              <p style={{ margin: '16px 0 0', fontSize: '13px', color: 'var(--Color-text-normal)', textAlign: 'center' }}>
                연수를 추가하면 방문자도 아래 목록에서 바로 볼 수 있습니다.
              </p>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default TrainingLobbyPage;
